/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import {
  RefreshCw, Plus, Upload, Download, Pencil, Archive, X,
  AlertTriangle, FileText, Search, Loader2, Beaker,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const STOCK_TONE = {
  in_stock: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  low_stock: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  out_of_stock: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  ordered: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  discontinued: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

const ERROR_TONE = {
  duplicate_conflict: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
  invalid_category: 'bg-rose-500/15 text-rose-200 border-rose-500/30',
  invalid_stock_status: 'bg-rose-500/15 text-rose-200 border-rose-500/30',
  invalid_reorder_time_days: 'bg-rose-500/15 text-rose-200 border-rose-500/30',
  missing_required_field: 'bg-rose-500/15 text-rose-200 border-rose-500/30',
  row_limit_exceeded: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

export default function FormulationRawBases() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [controlledLists, setControlledLists] = useState({ raw_base_categories: [], stock_statuses: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [filterCategory, setFilterCategory] = useState('__all__');
  const [filterStock, setFilterStock] = useState('__all__');
  const [filterActive, setFilterActive] = useState(true);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [editor, setEditor] = useState(null); // null | { mode:'create'|'edit', doc }
  const [csvOpen, setCsvOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);

  // ── Bootstrap controlled lists once ─────────────────────────
  useEffect(() => {
    axios.get(`${API}/formulation/controlled-lists`)
      .then((r) => setControlledLists(r.data || { raw_base_categories: [], stock_statuses: [] }))
      .catch(() => toast.error('Could not load controlled lists'));
  }, []);

  // ── Debounce search ─────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // ── Fetch list ──────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const params = { limit, offset, is_active: filterActive };
      if (debouncedQ) params.q = debouncedQ;
      if (filterCategory !== '__all__') params.category = filterCategory;
      if (filterStock !== '__all__') params.stock_status = filterStock;
      const r = await axios.get(`${API}/formulation/raw-bases`, { params });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load raw bases');
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, filterCategory, filterStock, filterActive, limit, offset]);
  useEffect(() => { load(); }, [load]);

  // ── Stat strip ──────────────────────────────────────────────
  const stats = useMemo(() => {
    const t = total;
    const active = items.filter((i) => i.is_active).length;
    const low = items.filter((i) => i.stock_status === 'low_stock').length;
    const out = items.filter((i) => i.stock_status === 'out_of_stock').length;
    return { total: t, active, low, out };
  }, [items, total]);

  const exportCsv = () => {
    window.open(`${API}/formulation/raw-bases/export.csv`, '_blank');
  };

  const clearFilters = () => {
    setQ(''); setDebouncedQ('');
    setFilterCategory('__all__'); setFilterStock('__all__');
    setFilterActive(true); setOffset(0);
  };

  if (!user) return null;

  return (
    <AdminLayout>
      <div data-testid="formulation-raw-bases-page" className="px-6 py-6 max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <Beaker className="w-6 h-6 text-cyan-300" />
            <h1 className="text-2xl font-semibold text-white">Raw Nutrient Base Library</h1>
            <Badge className="bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-[10px]">F1</Badge>
          </div>
          <p className="text-sm text-white/50">
            Foundation library for formulation production. Admin-managed.
          </p>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-white/60">
            <span><span className="text-white/40">total:</span> <span className="text-white">{stats.total}</span></span>
            <span>·</span>
            <span><span className="text-white/40">active on page:</span> <span className="text-emerald-300">{stats.active}</span></span>
            <span>·</span>
            <span><span className="text-white/40">low_stock:</span> <span className="text-amber-300">{stats.low}</span></span>
            <span>·</span>
            <span><span className="text-white/40">out_of_stock:</span> <span className="text-rose-300">{stats.out}</span></span>
          </div>
        </div>

        {/* FILTER ROW */}
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex flex-wrap items-center gap-2 mb-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              data-testid="raw-base-search-input"
              placeholder="Search code or name…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOffset(0); }}
              className="pl-8 h-9 bg-slate-950/40 border-white/10 text-sm"
            />
          </div>
          <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v); setOffset(0); }}>
            <SelectTrigger data-testid="raw-base-category-filter" className="w-56 h-9 bg-slate-950/40 border-white/10 text-xs">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {controlledLists.raw_base_categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStock} onValueChange={(v) => { setFilterStock(v); setOffset(0); }}>
            <SelectTrigger data-testid="raw-base-stock-filter" className="w-44 h-9 bg-slate-950/40 border-white/10 text-xs">
              <SelectValue placeholder="All stock" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All stock</SelectItem>
              {controlledLists.stock_statuses.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer pl-1">
            <input
              type="checkbox"
              data-testid="raw-base-active-toggle"
              checked={filterActive}
              onChange={(e) => { setFilterActive(e.target.checked); setOffset(0); }}
            />
            Active only
          </label>
          <div className="flex-1" />
          <Button
            size="sm" variant="outline"
            onClick={load}
            data-testid="raw-base-refresh-btn"
            className="border-white/15 text-white/70 hover:bg-white/5 h-9 text-xs"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* ACTION ROW */}
        {isAdmin && (
          <div className="flex flex-wrap justify-end gap-2 mb-3">
            <Button
              data-testid="raw-base-create-btn"
              onClick={() => setEditor({ mode: 'create', doc: blankDoc() })}
              className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 h-9 text-xs"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New Raw Base
            </Button>
            <Button
              data-testid="raw-base-csv-upload-btn"
              variant="outline"
              onClick={() => setCsvOpen(true)}
              className="border-white/15 text-white/80 hover:bg-white/5 h-9 text-xs"
            >
              <Upload className="w-4 h-4 mr-1.5" /> Upload CSV
            </Button>
            <Button
              data-testid="raw-base-csv-export-btn"
              variant="outline"
              onClick={exportCsv}
              className="border-white/15 text-white/80 hover:bg-white/5 h-9 text-xs"
            >
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        )}

        {/* CONTENT */}
        <div className="rounded-lg border border-white/10 bg-slate-900/40 overflow-hidden">
          {pageError && (
            <div data-testid="raw-base-page-error" className="p-6 text-center">
              <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
              <div className="text-sm text-white/80">Could not load raw bases</div>
              <div className="text-xs text-white/40 mt-1">{pageError}</div>
              <Button size="sm" variant="outline" className="mt-3 border-white/15" onClick={load}>Retry</Button>
            </div>
          )}

          {!pageError && loading && items.length === 0 && (
            <div data-testid="raw-base-loading" className="p-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-9 mb-2 rounded bg-white/[0.04] animate-pulse" />
              ))}
            </div>
          )}

          {!pageError && !loading && items.length === 0 && total === 0 && (
            <EmptyAll
              isAdmin={isAdmin}
              onCreate={() => setEditor({ mode: 'create', doc: blankDoc() })}
              onUpload={() => setCsvOpen(true)}
            />
          )}

          {!pageError && !loading && items.length === 0 && total > 0 && (
            <div data-testid="raw-base-empty-filtered" className="p-8 text-center">
              <FileText className="w-5 h-5 mx-auto text-white/40 mb-2" />
              <div className="text-sm text-white/70">No raw bases match your filters</div>
              <Button size="sm" variant="outline" className="mt-3 border-white/15" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}

          {!pageError && items.length > 0 && (
            <div className={`overflow-x-auto ${loading ? 'opacity-70' : ''}`}>
              <table data-testid="raw-base-table" className="w-full text-xs">
                <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Code</th>
                    <th className="text-left px-3 py-2 font-medium">Name</th>
                    <th className="text-left px-3 py-2 font-medium">Category</th>
                    <th className="text-left px-3 py-2 font-medium">Stock</th>
                    <th className="text-left px-3 py-2 font-medium">Reord</th>
                    <th className="text-left px-3 py-2 font-medium">Active</th>
                    <th className="text-left px-3 py-2 font-medium">Supplier</th>
                    <th className="text-left px-3 py-2 font-medium">Manufacturer</th>
                    <th className="text-left px-3 py-2 font-medium">Notes</th>
                    {isAdmin && <th className="text-right px-3 py-2 font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <Row
                      key={it.id}
                      it={it}
                      isAdmin={isAdmin}
                      onEdit={() => setEditor({ mode: 'edit', doc: it })}
                      onArchive={() => setArchiveTarget(it)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination footer */}
          {total > limit && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-white/10 text-[11px] text-white/50">
              <div>
                Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="border-white/15 h-7 text-[11px]"
                >
                  ← Prev
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  className="border-white/15 h-7 text-[11px]"
                >
                  Next →
                </Button>
              </div>
            </div>
          )}
        </div>

        {editor && (
          <RawBaseEditor
            mode={editor.mode}
            initial={editor.doc}
            controlledLists={controlledLists}
            onClose={() => setEditor(null)}
            onSaved={() => { setEditor(null); load(); }}
          />
        )}

        {csvOpen && (
          <CsvUploadDialog
            onClose={() => setCsvOpen(false)}
            onDone={() => { setCsvOpen(false); load(); }}
          />
        )}

        {archiveTarget && (
          <ArchiveConfirm
            target={archiveTarget}
            onCancel={() => setArchiveTarget(null)}
            onDone={() => { setArchiveTarget(null); load(); }}
          />
        )}
      </div>
    </AdminLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function blankDoc() {
  return {
    raw_base_code: '',
    raw_base_name: '',
    category: '',
    ingredients_extract_codes: [],
    reorder_time_days: 7,
    stock_status: 'in_stock',
    supplier_code: '',
    manufacturer_code: '',
    is_active: true,
    notes: '',
  };
}

function Row({ it, isAdmin, onEdit, onArchive }) {
  return (
    <tr
      data-testid={`raw-base-row-${it.id}`}
      className={`border-b border-white/5 hover:bg-white/[0.03] ${it.is_active ? '' : 'opacity-60'}`}
    >
      <td className="px-3 py-2 font-mono text-cyan-200 cursor-pointer" onClick={onEdit}>{it.raw_base_code}</td>
      <td className="px-3 py-2 text-white">{it.raw_base_name}</td>
      <td className="px-3 py-2">
        <span className="px-2 py-0.5 rounded border bg-slate-700/40 border-slate-600/40 text-slate-200 text-[10px]">
          {it.category}
        </span>
      </td>
      <td className="px-3 py-2">
        <span className={`px-2 py-0.5 rounded border text-[10px] ${STOCK_TONE[it.stock_status] || 'bg-slate-500/15 text-slate-300 border-slate-500/30'}`}>
          {it.stock_status}
        </span>
      </td>
      <td className="px-3 py-2 text-white/70">{it.reorder_time_days} d</td>
      <td className="px-3 py-2">{it.is_active ? '✓' : '✗'}</td>
      <td className="px-3 py-2 font-mono text-white/60">{it.supplier_code || '—'}</td>
      <td className="px-3 py-2 font-mono text-white/60">{it.manufacturer_code || '—'}</td>
      <td className="px-3 py-2 text-white/50 truncate max-w-[160px]" title={it.notes || ''}>{it.notes || '—'}</td>
      {isAdmin && (
        <td className="px-3 py-2 text-right">
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`raw-base-edit-btn-${it.id}`} className="h-7 px-2 text-cyan-300 hover:text-cyan-200">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          {it.is_active && (
            <Button size="sm" variant="ghost" onClick={onArchive} data-testid={`raw-base-archive-btn-${it.id}`} className="h-7 px-2 text-rose-300 hover:text-rose-200">
              <Archive className="w-3.5 h-3.5" />
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}

function EmptyAll({ isAdmin, onCreate, onUpload }) {
  return (
    <div data-testid="raw-base-empty-state" className="p-10 text-center">
      <Beaker className="w-8 h-8 text-white/30 mx-auto mb-3" />
      <div className="text-base text-white/80 mb-1">No raw nutrient bases yet</div>
      <p className="text-xs text-white/50 max-w-md mx-auto">
        The library is empty. Add your first raw base manually, or bulk-upload via CSV.
      </p>
      {isAdmin && (
        <div className="mt-4 flex gap-2 justify-center">
          <Button onClick={onCreate} className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 text-xs">
            <Plus className="w-4 h-4 mr-1.5" /> New Raw Base
          </Button>
          <Button onClick={onUpload} variant="outline" className="border-white/15 text-white/80 text-xs">
            <Upload className="w-4 h-4 mr-1.5" /> Upload CSV
          </Button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function RawBaseEditor({ mode, initial, controlledLists, onClose, onSaved }) {
  const [form, setForm] = useState({
    raw_base_code: initial.raw_base_code || '',
    raw_base_name: initial.raw_base_name || '',
    category: initial.category || '',
    ingredients_extract_codes: (initial.ingredients_extract_codes || []).join(', '),
    reorder_time_days: initial.reorder_time_days ?? 7,
    stock_status: initial.stock_status || 'in_stock',
    supplier_code: initial.supplier_code || '',
    manufacturer_code: initial.manufacturer_code || '',
    is_active: initial.is_active !== false,
    notes: initial.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [conflictField, setConflictField] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.raw_base_code.trim() || !form.raw_base_name.trim() || !form.category) {
      setValidationError('raw_base_code, raw_base_name, and category are required');
      return;
    }
    setSaving(true);
    setConflictField(null);
    setValidationError(null);
    try {
      const payload = {
        ...form,
        ingredients_extract_codes: form.ingredients_extract_codes
          .split(',').map((s) => s.trim()).filter(Boolean),
        reorder_time_days: Number(form.reorder_time_days) || 0,
      };
      if (mode === 'create') {
        await axios.post(`${API}/formulation/raw-bases`, payload);
        toast.success('Raw base created');
      } else {
        const { raw_base_code, ...patch } = payload;
        await axios.patch(`${API}/formulation/raw-bases/${initial.id}`, patch);
        toast.success('Raw base updated');
      }
      onSaved();
    } catch (e) {
      const data = e?.response?.data;
      // FastAPI's HTTPException(detail={...}) is wrapped one level deeper.
      const detailObj = data?.detail && typeof data.detail === 'object' ? data.detail : data;
      if (detailObj?.conflict_field) {
        setConflictField(detailObj.conflict_field);
        toast.error(detailObj.detail || 'Duplicate conflict');
      } else if (typeof detailObj === 'string') {
        setValidationError(detailObj);
        toast.error(detailObj);
      } else {
        const msg = detailObj?.detail || 'Save failed';
        setValidationError(msg);
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const fieldErr = (key) => conflictField === key
    ? 'border-rose-400/60 ring-1 ring-rose-400/40' : 'border-white/10';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="raw-base-form-modal"
        className="max-w-xl bg-slate-900 border-white/10 text-white"
      >
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New Raw Base' : `Edit ${initial.raw_base_code}`}</DialogTitle>
          <DialogDescription className="text-[11px] text-white/50">
            Required fields marked *. Category and stock-status are limited to the controlled list.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] text-white/60 mb-1 block">Code *</Label>
            <Input
              data-testid="raw-base-form-code"
              value={form.raw_base_code}
              disabled={mode === 'edit'}
              onChange={(e) => set('raw_base_code', e.target.value)}
              className={`h-9 text-xs bg-white/5 ${fieldErr('raw_base_code')}`}
            />
            {conflictField === 'raw_base_code' && (
              <div data-testid="raw-base-form-error-raw_base_code" className="text-[11px] text-rose-300 mt-1">
                Already exists
              </div>
            )}
          </div>
          <div>
            <Label className="text-[11px] text-white/60 mb-1 block">Name *</Label>
            <Input
              data-testid="raw-base-form-name"
              value={form.raw_base_name}
              onChange={(e) => set('raw_base_name', e.target.value)}
              className={`h-9 text-xs bg-white/5 ${fieldErr('raw_base_name')}`}
            />
            {conflictField === 'raw_base_name' && (
              <div data-testid="raw-base-form-error-raw_base_name" className="text-[11px] text-rose-300 mt-1">
                Already exists
              </div>
            )}
          </div>
          <div>
            <Label className="text-[11px] text-white/60 mb-1 block">Category *</Label>
            <Select value={form.category} onValueChange={(v) => set('category', v)}>
              <SelectTrigger data-testid="raw-base-form-category" className="h-9 text-xs bg-white/5 border-white/10">
                <SelectValue placeholder="Select category…" />
              </SelectTrigger>
              <SelectContent>
                {controlledLists.raw_base_categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-white/60 mb-1 block">Stock status</Label>
              <Select value={form.stock_status} onValueChange={(v) => set('stock_status', v)}>
                <SelectTrigger data-testid="raw-base-form-stock-status" className="h-9 text-xs bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {controlledLists.stock_statuses.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-white/60 mb-1 block">Reorder time (days)</Label>
              <Input
                type="number" min="0"
                data-testid="raw-base-form-reorder"
                value={form.reorder_time_days}
                onChange={(e) => set('reorder_time_days', e.target.value)}
                className="h-9 text-xs bg-white/5 border-white/10"
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-white/60 mb-1 block">Ingredients extract codes (comma-separated)</Label>
            <Input
              data-testid="raw-base-form-extracts"
              value={form.ingredients_extract_codes}
              onChange={(e) => set('ingredients_extract_codes', e.target.value)}
              placeholder="EX-B12-A, EX-B12-CO"
              className="h-9 text-xs bg-white/5 border-white/10"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-white/60 mb-1 block">Supplier code</Label>
              <Input
                data-testid="raw-base-form-supplier"
                value={form.supplier_code}
                onChange={(e) => set('supplier_code', e.target.value)}
                className="h-9 text-xs bg-white/5 border-white/10"
              />
            </div>
            <div>
              <Label className="text-[11px] text-white/60 mb-1 block">Manufacturer code</Label>
              <Input
                data-testid="raw-base-form-manufacturer"
                value={form.manufacturer_code}
                onChange={(e) => set('manufacturer_code', e.target.value)}
                className="h-9 text-xs bg-white/5 border-white/10"
              />
            </div>
          </div>
          {mode === 'edit' && (
            <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
              <input
                type="checkbox"
                data-testid="raw-base-form-active"
                checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
              />
              Active
            </label>
          )}
          <div>
            <Label className="text-[11px] text-white/60 mb-1 block">Notes</Label>
            <Textarea
              data-testid="raw-base-form-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="text-xs bg-white/5 border-white/10"
            />
          </div>
          {validationError && (
            <div className="text-[11px] text-rose-300">{validationError}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button
            data-testid="raw-base-form-save-btn"
            onClick={submit}
            disabled={saving || !form.raw_base_code.trim() || !form.raw_base_name.trim() || !form.category}
            className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Save Raw Base
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function CsvUploadDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('insert_only');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [fatalError, setFatalError] = useState(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setFatalError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mode', mode);
      const r = await axios.post(`${API}/formulation/raw-bases/upload-csv`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not process file';
      setFatalError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null); setResult(null); setFatalError(null);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="raw-base-csv-modal"
        className="max-w-2xl bg-slate-900 border-white/10 text-white"
      >
        <DialogHeader>
          <DialogTitle>{result ? 'CSV Upload Result' : (fatalError ? 'CSV Upload Failed' : 'Upload Raw Bases CSV')}</DialogTitle>
          <DialogDescription className="text-[11px] text-white/50">
            {result ? 'Review per-row results below. Failing rows must be fixed and re-uploaded.'
              : 'Max 1000 rows · 2 MB · UTF-8 encoded.'}
          </DialogDescription>
        </DialogHeader>

        {!result && !fatalError && (
          <div className="space-y-3">
            <div className="rounded border border-dashed border-white/15 p-4 bg-white/[0.02]">
              <input
                type="file"
                accept=".csv,text/csv"
                data-testid="raw-base-csv-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-xs text-white/70"
              />
              {file && (
                <div className="mt-2 text-[11px] text-white/60">
                  Selected: <span className="text-white">{file.name}</span> · {Math.round(file.size / 1024)} KB
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] text-white/60 mb-1">Mode</div>
              <div className="flex gap-3 text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    data-testid="raw-base-csv-mode-insert_only"
                    checked={mode === 'insert_only'}
                    onChange={() => setMode('insert_only')}
                  />
                  insert_only <span className="text-white/40">(recommended — never overwrites)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    data-testid="raw-base-csv-mode-upsert_by_code"
                    checked={mode === 'upsert_by_code'}
                    onChange={() => setMode('upsert_by_code')}
                  />
                  upsert_by_code <span className="text-white/40">(advanced)</span>
                </label>
              </div>
            </div>
            <div className="text-[11px] text-white/40 leading-snug">
              Columns (in order): raw_base_code, raw_base_name, category, ingredients_extract_codes (semicolon-sep),
              reorder_time_days, stock_status, supplier_code, manufacturer_code, is_active, notes.
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-xs">
              <Stat data-testid="raw-base-csv-result-inserted" label="Inserted" value={result.inserted} tone="emerald" />
              <Stat label="Updated" value={result.updated} tone="sky" />
              <Stat label="Errors" value={(result.errors || []).length} tone={(result.errors || []).length ? 'rose' : 'slate'} testId="raw-base-csv-result-errors" />
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="rounded border border-white/10 bg-slate-950/40 max-h-72 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-950 border-b border-white/10 text-white/50 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Row</th>
                      <th className="text-left px-2 py-1.5">Code</th>
                      <th className="text-left px-2 py-1.5">Reason</th>
                      <th className="text-left px-2 py-1.5">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((er, i) => (
                      <tr key={i} data-testid={`raw-base-csv-error-row-${i}`} className="border-b border-white/5">
                        <td className="px-2 py-1.5 text-white/60">{er.row}</td>
                        <td className="px-2 py-1.5 font-mono text-white/80">{er.raw_base_code || '—'}</td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded border text-[10px] ${ERROR_TONE[er.error] || 'bg-slate-500/15 text-slate-300 border-slate-500/30'}`}>
                            {er.error}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-white/60">{er.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {fatalError && (
          <div className="rounded border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-200">
            <div className="flex items-center gap-2 font-medium">
              <X className="w-3.5 h-3.5" /> Could not process file
            </div>
            <div className="text-[11px] text-rose-200/80 mt-1">{fatalError}</div>
          </div>
        )}

        <DialogFooter>
          {!result && !fatalError && (
            <>
              <Button variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
              <Button
                data-testid="raw-base-csv-submit-btn"
                onClick={submit}
                disabled={!file || busy}
                className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                Upload &amp; Parse
              </Button>
            </>
          )}
          {result && (
            <>
              <Button variant="ghost" onClick={reset} className="text-white/60">Upload another</Button>
              <Button onClick={onDone} className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30">Done</Button>
            </>
          )}
          {fatalError && (
            <>
              <Button variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
              <Button onClick={reset} className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30">Try again</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone = 'slate', testId }) {
  const tones = {
    emerald: 'bg-emerald-500/10 text-emerald-200 border-emerald-500/30',
    sky: 'bg-sky-500/10 text-sky-200 border-sky-500/30',
    rose: 'bg-rose-500/10 text-rose-200 border-rose-500/30',
    slate: 'bg-slate-500/10 text-slate-300 border-slate-500/30',
  };
  return (
    <div data-testid={testId} className={`rounded border p-2 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function ArchiveConfirm({ target, onCancel, onDone }) {
  const [busy, setBusy] = useState(false);
  const archive = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/formulation/raw-bases/${target.id}/archive`);
      toast.success('Archived');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-sm bg-slate-900 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Archive raw base?</DialogTitle>
          <DialogDescription className="text-[11px] text-white/50">
            <span className="text-white">{target.raw_base_name}</span> ({target.raw_base_code}) will be marked inactive. It can be reactivated via Edit.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} className="text-white/60">Cancel</Button>
          <Button
            data-testid="raw-base-archive-confirm-btn"
            onClick={archive}
            disabled={busy}
            className="bg-rose-500/20 text-rose-200 border border-rose-500/30 hover:bg-rose-500/30"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Archive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
